/**
 * @Description 打靶任务详情
 */

import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { isArray, refactorPermissions } from '@lhb/func';
import { deletePractice, practiceDetail, practiceEdit } from '@/common/api/practice';
import { V2Confirm } from '@/common/components/Others/V2Confirm';
import { Button, Form } from 'antd';
// import cs from 'classnames';
import styles from './index.module.less';
import V2Drawer from '@/common/components/Feedback/V2Drawer';
import SkeletonMapDetail from '@/common/components/Skeleton/SkeletonMapDetail';
import V2Container from '@/common/components/Data/V2Container';
import Main from './Main';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Operate from '@/common/components/Others/V2Operate';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import UserInfoContext from '@/layout/context';
import { globalCheckInFieldAAlias } from '@/common/utils/ways';
import { useBigdataFn } from '@/common/utils/bigdata';

interface PracticeDetailFDrawerProps {
  [p:string]: any;
}
const PracticeDetailFDrawer: FC<PracticeDetailFDrawerProps> = ({
  drawerData,
  setDrawerData,
  handleClose,
  onRefresh,
  isNonstopRef, // 是否是直接点编辑进来的
  targetPunchInAlias,
}) => {
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook
  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息
  const [form] = Form.useForm();
  const formLockRef: any = useRef(false);
  const { id, open, isEdit } = drawerData || {};
  const [loading, setLoading] = useState<boolean>(true); // 加载中
  const [detail, setDetail] = useState<any>(null); // 详情
  const [permissions, setPermissions] = useState<any[]>([]);
  // 注意，在选址地图中，别名取的是globalAliasItemVOs里的，其他的是打靶统计和打靶任务列表传入的别名
  const targetPunchInAliasVal = useMemo(() => {
    return targetPunchInAlias || globalCheckInFieldAAlias(globalAliasItemVOs);
  }, [targetPunchInAlias, globalAliasItemVOs]);

  useEffect(() => {
    if (!(open && id)) return;
    methods.loadDetail();
  }, [open, id]);

  const methods = useMethods({
    async loadDetail() {
      const data = await practiceDetail({ taskId: id });
      const { permissions } = data || {};
      // 按钮顺序
      if (isArray(permissions) && permissions.length) {
        const btns: any[] = [];
        const editBtn = permissions.find((item) => item.event === 'checkIn:task:edit');
        const delBtn = permissions.find((item) => item.event === 'checkIn:task:delete');
        delBtn && btns.push({ ...delBtn, type: 'default' });
        editBtn && btns.push({ ...editBtn, type: 'primary' });
        setPermissions(btns);
      }
      setDetail(data);
      setLoading(false);
    },
    handleEdit: () => {
      bigdataFn('efab59f9-8b21-488b-bed6-c2d7cbaf43e6', '打卡详情编辑', '编辑了打卡详情', { op_block: '', op_event: `${detail.address}`, });
      setDrawerData((state) => ({ ...state, isEdit: true }));
    },
    handleDelete: () => {
      bigdataFn('3a08f48f-02f4-4a91-8339-3628dab0b5e2', '删除打卡详情', '删除了打卡详情', { op_block: '', op_event: ``, });
      V2Confirm({
        content: `点击删除后，该任务以及其${targetPunchInAliasVal}记录将会不可见`,
        onOk: () => {
          const params: any = {
            id: detail.id
          };
          deletePractice(params).then(() => {
            bigdataFn('81fcb976-09b4-45ba-8920-11d05db9dd0a', '成功删除打卡详情', '成功删除了打卡详情', { op_block: '', op_event: `${detail.address}`, });
            methods.handleCloseBefore();
            V2Message.success('删除成功');
            onRefresh && onRefresh();
          });
        },
      });
    },
    handleEditSubmit: () => {
      form.validateFields().then((values) => {
        if (formLockRef.current) return;
        formLockRef.current = true;
        const params = {
          id: detail.id,
          ...values,
        };
        practiceEdit(params).then(() => {
          V2Message.success(`编辑成功`);
          // 更新列表
          onRefresh && onRefresh();
          if (isNonstopRef && isNonstopRef.current) {
            methods.handleCloseBefore();
            return;
          }
          setDrawerData((state) => ({ ...state, isEdit: false }));
          // 更新详情
          methods.loadDetail();
          formLockRef.current = false;
        }).catch(() => {
          formLockRef.current = false;
        });
      });
    },
    handleCloseBefore: () => {
      bigdataFn('81d35eb7-0031-4b1f-8a03-10b9ae900114', '关闭打卡详情', '关闭了打卡详情', { op_block: '', op_event: ``, });
      isEdit && form && form.resetFields();
      handleClose && handleClose();
    }
  });
  return (
    <V2Drawer
      open={open}
      destroyOnClose
      onClose={methods.handleCloseBefore}
      className={styles.detailDrawer}
    >
      {loading
        ? <SkeletonMapDetail/>
        : <V2Container
          // 容器上下padding 0， 所以减去就是0
          style={{ height: 'calc(100vh - 48px)' }}
          extraContent={{
            top: <V2Title
              text={isEdit ? '编辑任务' : `${targetPunchInAliasVal}详情`}
              extra={ isEdit ? <></>
                : <V2Operate
                  showBtnCount={2}
                  operateList={refactorPermissions(isArray(permissions) && permissions.length ? permissions : [])}
                  // demo数据 { name: '删除', event: 'checkIn:task:delete', type: 'default' }, { name: '编辑', event: 'checkIn:task:edit', type: 'primary' }
                  onClick={(btn: any) => methods[btn.func]()}
                />}>
              <span className='bold'>{isEdit ? '编辑任务' : `${targetPunchInAliasVal}详情`}</span>
            </V2Title>,
            bottom: <>{
              isEdit ? <>
                <div className='rt'>
                  <Button type='default' onClick={() => {
                    if (isNonstopRef && isNonstopRef.current) { // 直接打开的编辑
                      methods.handleCloseBefore();
                      return;
                    }
                    setDrawerData((state) => ({ ...state, isEdit: false }));
                  }}>取消</Button>
                  <Button type='primary' className='ml-12' onClick={() => methods.handleEditSubmit()}>确定</Button>
                </div>
              </> : <></>
            }</>
          }}
        >
          <Main
            detail={detail}
            isEdit={isEdit}
            form={form}
            targetPunchInAlias={targetPunchInAliasVal}
          />
        </V2Container>
      }
    </V2Drawer>
  );
};

export default PracticeDetailFDrawer;
